<template>
  <BaseModal
    id="modal-create-or-update-criteria"
    :title="criteria ? 'Edit a criteria' : 'Create a criteria'"
    size="xl"
    :show.sync="$show"
  >
    <div v-show="showSectionAlign" class="my-4">
      <label for="select__ultimate-lo">Align to learning outcome</label>
      <b-form-select
        v-model="learningOutcomeId"
        name="select__ultimate-lo"
        class="base__select-aligned-lo"
        :options="optionLearningOutcomes"
        :disabled="disabled"
      ></b-form-select>
    </div>

    <div class="mb-4">
      <label for="input__title">Title</label>
      <b-form-input
        v-model="$v.title.$model"
        name="input__title"
        class="input__title"
        :class="{
          'is-invalid': $v.title.$error,
          'is-valid': !$v.title.$invalid
        }"
        :disabled="disabled"
      ></b-form-input>
      <b-form-invalid-feedback v-if="!$v.title.required">
        Title is required.
      </b-form-invalid-feedback>
    </div>

    <div class="ml-4">
      <label for="input__pass-criterion">Pass</label>
      <b-form-textarea
        v-model="$v.criteriaPass.$model"
        name="input__pass-criterion"
        class="input__pass-criterion"
        rows="4"
        :class="{
          'is-invalid': $v.criteriaPass.$error,
          'is-valid': !$v.criteriaPass.$invalid
        }"
        :disabled="disabled"
      ></b-form-textarea>
      <b-form-invalid-feedback v-if="!$v.criteriaPass.required">
        Pass is required.
      </b-form-invalid-feedback>

      <label for="input__not-yet-criterion" class="mt-4">Not yet</label>
      <b-form-textarea
        v-model="$v.criteriaNotYet.$model"
        name="input__not-yet-criterion"
        class="input__not-yet-criterion"
        rows="4"
        :class="{
          'is-invalid': $v.criteriaNotYet.$error,
          'is-valid': !$v.criteriaNotYet.$invalid
        }"
        :disabled="disabled"
      ></b-form-textarea>
      <b-form-invalid-feedback v-if="!$v.criteriaNotYet.required">
        Not yet is required.
      </b-form-invalid-feedback>
    </div>

    <template #modal-footer>
      <div class="d-flex justify-content-start w-100">
        <b-button class="btn__submit" variant="success" @click="onSubmit">
          <b-spinner v-if="submitting" />
          <span v-else> {{ criteria ? 'Save' : 'Create' }}</span>
        </b-button>
        <b-button
          variant="link"
          class="btn__cancel btn-link-dark"
          :disabled="disabled"
          @click="hide"
        >
          Cancel
        </b-button>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import LearningOutcome from '@/models/LearningOutcome';
import Criteria from '@/models/Criteria';
import BaseModal from '@/components/base-components/BaseModal';

export default {
  components: {
    BaseModal
  },
  mixins: [validationMixin],

  props: {
    ultimateLearningOutcome: {
      type: LearningOutcome,
      required: true
    },
    subLearningOutcome: {
      type: LearningOutcome,
      default: null
    },
    criteria: {
      type: Criteria,
      default: null
    },
    show: {
      type: Boolean,
      default: false
    },

    showSectionAlign: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      submitting: false,
      disabled: false,
      title: '',
      criteriaPass: '',
      criteriaNotYet: '',
      learningOutcomeId: null
    };
  },

  validations() {
    return {
      title: { required },
      criteriaPass: { required },
      criteriaNotYet: { required }
    };
  },

  computed: {
    $show: {
      get() {
        return this.show;
      },

      set(v) {
        this.$emit('update:show', v);
      }
    },

    defaultLearningOutcome() {
      return LearningOutcome.find(this.learningOutcomeId);
    },

    learningOutcomes() {
      return LearningOutcome.findIn(this.ultimateLearningOutcome.sub_learning_outcome_ids);
    },

    optionLearningOutcomes() {
      const ultimateLearningOutcome = this.ultimateLearningOutcome
        ? [
            {
              value: this.ultimateLearningOutcome.id,
              text: this.ultimateLearningOutcome.title
            }
          ]
        : [];

      let subLearningOutcomes = [];

      if (this.learningOutcomes && this.learningOutcomes.length > 0) {
        subLearningOutcomes = this.learningOutcomes.map((learningOutcome) => {
          return { value: learningOutcome.id, text: learningOutcome.title };
        });
      }

      return [...ultimateLearningOutcome, ...subLearningOutcomes];
    }
  },

  watch: {
    show: {
      immediate: true,
      handler(show) {
        if (show) {
          this.learningOutcomeId = this.criteria
            ? this.criteria.learning_outcome_id
            : this.subLearningOutcome
            ? this.subLearningOutcome.id
            : this.ultimateLearningOutcome.id;

          this.title = this.criteria ? this.criteria.title : 'Performance Criteria';
          this.criteriaPass = this.criteria ? this.criteria.pass_criterion : '';

          this.criteriaNotYet = this.criteria ? this.criteria.not_yet_criterion : '';
        }
      }
    }
  },

  methods: {
    async onSubmit() {
      this.$v.$touch();

      this.invalid = false;

      if (this.$v.$invalid) {
        this.invalid = true;

        return this.$toast.error(
          `Cannot ${this.criteria ? 'update' : 'create'} criteria, please check error messages`
        );
      }

      let promise;

      const { title, criteriaPass, criteriaNotYet, learningOutcomeId } = this;

      try {
        this.disabled = true;
        this.submitting = true;

        const formData = {
          title,
          pass_criterion: criteriaPass,
          not_yet_criterion: criteriaNotYet,
          learning_outcome: learningOutcomeId,
          description: ''
        };

        if (this.criteria) {
          const { learning_outcome_id: oldLearningOutcomeId } = this.criteria;

          await Criteria.api().update(this.criteria.id, formData);

          if (oldLearningOutcomeId != learningOutcomeId) {
            const oldLearningOutcome = LearningOutcome.find(oldLearningOutcomeId);

            let { criterion_ids = [] } = oldLearningOutcome;

            const criteriaIndex = criterion_ids.findIndex(
              (criterion_id) => criterion_id == this.criteria.id
            );

            if (criteriaIndex !== -1) {
              criterion_ids.splice(criteriaIndex, 1);

              LearningOutcome.update({
                where: oldLearningOutcomeId,
                data: {
                  criterion_ids
                }
              });
            }

            let { properties = '{}' } = this.ultimateLearningOutcome;

            properties = JSON.parse(properties);

            const { order = [] } = properties;

            if (oldLearningOutcomeId == this.ultimateLearningOutcome.id) {
              const orderIndex = order.findIndex(
                (subOrder) => subOrder.id == this.criteria.id && subOrder.model == 'criterias'
              );

              if (orderIndex !== -1) {
                order.splice(orderIndex, 1);
              }

              const newOrderIndex = order.findIndex((subOrder) => subOrder.id == learningOutcomeId);

              const subOrder = order[newOrderIndex];

              if (subOrder) {
                const { children = [] } = subOrder;

                children.push({ id: this.criteria.id, model: 'criterias' });

                order.splice(newOrderIndex, 1, subOrder);
              }
            } else {
              const orderIndex = order.findIndex((subOrder) => subOrder.id == oldLearningOutcomeId);

              if (orderIndex !== -1) {
                const subOrder = order[orderIndex];

                if (subOrder) {
                  const { children = [] } = subOrder;

                  const childrenIndex = children.findIndex((child) => {
                    return child.id == this.criteria.id;
                  });

                  if (childrenIndex !== -1) {
                    children.splice(childrenIndex, 1);

                    this.$set(order[orderIndex], 'children', children);
                  }
                }
              }

              if (learningOutcomeId == this.ultimateLearningOutcome.id) {
                order.push({ id: this.criteria.id, model: 'criterias' });
              } else {
                const newOrderIndex = order.findIndex(
                  (subOrder) => subOrder.id == learningOutcomeId
                );

                const subOrder = order[newOrderIndex];

                if (subOrder) {
                  const { children = [] } = subOrder;

                  children.push({ id: this.criteria.id, model: 'criterias' });

                  order.splice(newOrderIndex, 1, subOrder);
                }
              }
            }

            await LearningOutcome.api().updateProperties(this.ultimateLearningOutcome.id, {
              ...properties,
              order
            });
          }

          this.$emit('update', {
            id: this.criteria.id,
            learningOutcomeId,
            oldLearningOutcomeId,
            data: formData
          });
        } else {
          promise = await Criteria.api().create(formData);

          const { id } = promise.response.data;

          const targetLearningOutcome = LearningOutcome.find(learningOutcomeId);

          const { criterion_ids = [] } = targetLearningOutcome;

          LearningOutcome.update({
            where: learningOutcomeId,
            data: {
              criterion_ids: [...criterion_ids, id]
            }
          });

          let { id: ultimateLearningOutcomeId, properties = '{}' } = this.ultimateLearningOutcome;

          properties = JSON.parse(properties);

          const { order = [] } = properties;

          if (learningOutcomeId == ultimateLearningOutcomeId) {
            order.push({ id, model: 'criterias' });
          } else {
            const insertedOrderIndex = order.findIndex(
              (subOrder) => subOrder.id == learningOutcomeId
            );

            const subOrder = order[insertedOrderIndex];

            if (subOrder) {
              const { children = [] } = subOrder;

              children.push({ id, model: 'criterias' });

              order.splice(insertedOrderIndex, 1, subOrder);
            }
          }

          await LearningOutcome.api().updateProperties(ultimateLearningOutcomeId, {
            ...properties,
            order
          });

          this.$emit('create', {
            id,
            learningOutcomeId,
            data: formData
          });
        }

        this.$toast.success(
          `The criteria was ${this.criteria ? 'updated' : 'created'} successfully`
        );

        this.$emit('submit', formData);

        this.hide();
      } catch (error) {
        this.disabled = false;

        this.$toast.error(`Cannot ${this.criteria ? 'update' : 'create'} an sub-learning outcome`);
      } finally {
        this.disabled = false;
        this.submitting = false;
      }

      return promise;
    },
    // show() {
    //   this.$bvModal.show(`modal-create-or-update-criteria`);
    // },
    hide() {
      this.$show = false;

      this.clearDate();
    },
    clearDate() {
      this.$v.$reset();
      this.title = '';
      this.criteriaPass = '';
      this.criteriaNotYet = '';
    }
  }
};
</script>
